<template>
	<section>
		<div class="custom-topbar">
			<div class="badge mode-indicator">
				<i class="fas fa-cog" :class="{'fa-pulse': type === 'realtime'}"></i> 
				Modo de Dados:
				<span v-if="type === 'realtime'" class="realtime">Tempo Real</span>
				<span v-else class="report">Relatório</span>
			</div>
			<div>
				<b-button variant="success" @click="generateExcel()" :disabled="excelLoading">
					<span v-if="!excelLoading">
						<i class="fas fa-clipboard"></i> Exportar para Excel
					</span>
					<span v-else>
						<i class="fas fa-spinner fa-pulse"></i> Gerando arquivo...
					</span>
				</b-button>
				<b-button v-b-toggle.collapse-dashboard-filters variant="primary">
					<i class="fas fa-filter"></i> Filtros
				</b-button>
			</div>
		</div>

		<div class="collapse-wrapper">
			<b-collapse id="collapse-dashboard-filters" class="filters-collapse">
				<b-card class="filters-card">
					<div class="filters-content">
						<div class="filter-group">
							<span>Filas de telefonia</span>
							<multiselect v-model="selectedQueues" :options="queuesList" :multiple="true" :close-on-select="false"
								:clear-on-select="false" select-label="Selecionar" deselect-label="Remover" selected-label="Selecionada"
								:preserve-search="true" placeholder="Selecione as filas" label="name" track-by="id"
								:preselect-first="true" :searchable="true" style="font-size: 12px;">
								<template slot="selection" slot-scope="{ isOpen }">
									<span class="multiselect__single"
										v-if="selectedQueues.length" 
										v-show="!isOpen">{{ selectedQueues.length }} {{ selectedQueues.length === 1 ? 'fila selecionada' : 'filas selecionadas' }}
									</span></template>
							</multiselect>
						</div>
						<div class="filter-group">
							<span>Filas de chat</span>
							<multiselect v-model="selectedChatQueues" :options="chatQueuesList" :multiple="true" :close-on-select="false"
								:clear-on-select="false" select-label="Selecionar" deselect-label="Remover" selected-label="Selecionada"
								:preserve-search="true" placeholder="Selecione as filas" label="name" track-by="id"
								:preselect-first="true" :searchable="true" style="font-size: 12px;">
								<template slot="selection" slot-scope="{ isOpen }">
									<span class="multiselect__single"
										v-if="selectedChatQueues.length" 
										v-show="!isOpen">{{ selectedChatQueues.length }} {{ selectedChatQueues.length === 1 ? 'fila selecionada' : 'filas selecionadas' }}
									</span></template>
							</multiselect>
						</div>
						<div class="filter-group">
							<span>Modo de dados</span>
							<div class="type-select-group">
								<div class="radio-group">
									<input type="radio" data-type="horizontal" class="form-control" name="filter-select" id="date-filter"
										value="realtime" v-model="type">
									<label for="date-filter">Tempo Real</label>
								</div>
								<div class="radio-group">
									<input type="radio" data-type="horizontal" class="form-control" name="filter-select" id="date-filter"
										value="report" v-model="type">
									<label for="date-filter">Relatório</label>
								</div>
								<div class="date-group">
									<div>
										<span>De</span>
										<input type="date" class="form-control" :disabled="type === 'realtime'" v-model="start" />
									</div>
									<div>
										<span>Até</span>
										<input type="date" class="form-control" :disabled="type === 'realtime'" v-model="end" />
									</div>
								</div>
							</div>
						</div>
						<div class="button-group">
							<b-button @click="resetFilters()" variant="secondary"><i class="fas fa-refresh"></i>
								Resetar
							</b-button>
							<b-button @click="applyFilters()" variant="primary"><i class="fas fa-bit fa-check"></i>
								Aplicar
							</b-button>
						</div>
					</div>
					<b-button @click="closeFilters()" class="close-filter-button"><i class="fas fa-bit fa-times"></i>
						Fechar 
					</b-button>
				</b-card>
			</b-collapse>
		</div>
		<div v-if="isLoading" class="loading-container">
			<div class="loading-content">
				<i class="fas fa-spinner fa-pulse loading-icon"></i>
				<p class="loading-text">Carregando, por favor aguarde...</p>
			</div>
		</div>
		<div v-else class="dashboard-container">
			<div class="custom-panel left-panel">
				<div class="horizontal-container chart-sm">
					<div class="chart-container chart-lg no-overflow">
						<InfoCard :seriesTitle="'Ligações recebidas'" :data="chartData.cards.received" :seriesColor="'#023e8a'" />
					</div>
					<div class="chart-container chart-lg no-overflow">
						<InfoCard :seriesTitle="'Ligações atendidas'" :data="chartData.cards.answered" :seriesColor="'#0ead69'" />
					</div>
					<div class="chart-container chart-lg no-overflow">
						<InfoCard :seriesTitle="'Ligações abandonadas'" :data="chartData.cards.abandoned" :seriesColor="'#ef233c'" />
					</div>
					<div class="chart-container chart-lg no-overflow">
						<InfoCard :seriesTitle="'Ligações realizadas'" :data="chartData.cards.outgoing" :seriesColor="'#7b2cbf'" />
					</div>
				</div>
				<div class="chart-container chart-lg no-overflow">
					<OperatorsTable :data="chartData.operators" />
				</div>
				<div class="horizontal-container chart-md">
					<div class="chart-container chart-lg">
						<div class="toggle-chart-buttons">
							<div class="badge mode-icon" :title="dynamicChart.requests.mode === 'fixed' ? 'Modo Fixo' : 'Modo Automático'" @click="changeChartMode('requests')">
								<i 
									class="fas" 
									:class="{'fa-bar-chart': dynamicChart.requests.mode === 'fixed', 'fa-random': dynamicChart.requests.mode === 'auto'}"
								></i>
								{{ dynamicChart.requests.mode === 'fixed' ? 'Fixo' : 'Auto' }}
							</div>
							<div 
								class="badge change-icon"
								@click="changeChartDisplay('requests')"
								>
								<i class="fas fa-refresh"></i>Alternar
							</div>
						</div>
						<AttendanceReasonChart v-if="dynamicChart.requests.display === 1" :data="chartData.orders" :title="'Pedidos'" />
						<Requests v-if="dynamicChart.requests.display === 2" :data="chartData.requests.channels" />
						<!-- <SatisfactionChart v-if="dynamicChart.survey.display === 1" :data="chartData.survey.phone" :title="'Pesquisa de Satisfação - Telefonia'" />
						<AttendanceReasonChart v-if="dynamicChart.survey.display === 2" :data="chartData.reasons.phone" :title="'Motivo da Avaliação - Telefonia'" />
						<SatisfactionChart v-if="dynamicChart.survey.display === 3" :data="chartData.survey.chat" :title="'Pesquisa de Satisfação - Whatsapp'" />
						<AttendanceReasonChart v-if="dynamicChart.survey.display === 4" :data="chartData.reasons.chat" :title="'Motivo da Insatisfação - Whatsapp'" /> -->
					</div>
					<div class="chart-container chart-lg">
						<div class="toggle-chart-buttons">
							<div class="badge mode-icon" :title="dynamicChart.channels.mode === 'fixed' ? 'Modo Fixo' : 'Modo Automático'" @click="changeChartMode('channels')">
								<i 
									class="fas" 
									:class="{'fa-bar-chart': dynamicChart.channels.mode === 'fixed', 'fa-random': dynamicChart.channels.mode === 'auto'}"
								></i>
								{{ dynamicChart.channels.mode === 'fixed' ? 'Fixo' : 'Auto' }}
							</div>
							<div 
								class="badge change-icon"
								@click="changeChartDisplay('channels')"
							>
								<i class="fas fa-refresh"></i>Alternar
							</div>
						</div>
						<AttendanceChannel v-if="dynamicChart.channels.display === 1" :data="chartData.channels" />
						<AttendanceChannel v-if="dynamicChart.channels.display === 2" :data="chartData.requests.status" :title="'Status de Solicitações'" :type="'requestStatus'" />
						<ReopenedRequests v-if="dynamicChart.channels.display === 3" :data="chartData.requests.reopened" />
					</div>
				</div>
			</div>
			<div class="custom-panel right-panel">
				<div class="chart-container chart-lg">
					<ReceivedCallsChart :data="chartData.receivedCalls" />
				</div>
				<div class="chart-container chart-md">
					<!-- <div class="toggle-chart-buttons">
						<div class="badge mode-icon" :title="dynamicChart.levels.mode === 'fixed' ? 'Modo Fixo' : 'Modo Automático'" @click="changeChartMode('levels')">
							<i 
								class="fas" 
								:class="{'fa-bar-chart': dynamicChart.levels.mode === 'fixed', 'fa-random': dynamicChart.levels.mode === 'auto'}"
							></i>
							{{ dynamicChart.levels.mode === 'fixed' ? 'Fixo' : 'Auto' }}
						</div>
						<div 
							class="badge change-icon"
							@click="changeChartDisplay('levels')"
						>
							<i class="fas fa-refresh"></i>Alternar
						</div>
					</div> -->
					<AbandonedLevelChart v-if="dynamicChart.levels.display === 1" :data="chartData.abandonLevel"/>
					<!-- <ServiceLevelChart v-if="dynamicChart.levels.display === 2" :data="chartData.serviceLevel" /> -->
					<!-- REMOVIDO: CsatChart telefonia e chat
					<CsatChart v-if="dynamicChart.levels.display === 3" :data="chartData.survey.phone" :title="'CSAT - Telefonia'"/>
					<CsatChart v-if="dynamicChart.levels.display === 4" :data="chartData.survey.chat" :title="'CSAT - Chat'"/>
					-->
				</div>
				<div class="chart-container chart-lg">
					<div class="toggle-chart-buttons">
						<div class="badge mode-icon" :title="dynamicChart.queue.mode === 'fixed' ? 'Modo Fixo' : 'Modo Automático'" @click="changeChartMode('queue')">
							<i 
								class="fas" 
								:class="{'fa-bar-chart': dynamicChart.queue.mode === 'fixed', 'fa-random': dynamicChart.queue.mode === 'auto'}"
							></i>
							{{ dynamicChart.queue.mode === 'fixed' ? 'Fixo' : 'Auto' }}
						</div>
						<div 
							class="badge change-icon"
							@click="changeChartDisplay('queue')"
						>
							<i class="fas fa-refresh"></i>Alternar
						</div>
					</div>
					<QueueTotals v-if="dynamicChart.queue.display === 1" :data="chartData.queues" />
					<QueueAvgTimeChart v-if="dynamicChart.queue.display === 2" :data="chartData.queues" />
				</div>
			</div>
		</div>
		<transition name="fade-float">
			<div
				v-if="type === 'realtime' && reopenedList.length"
				class="reopened-float-wrapper"
			>
				<transition name="slide-up">
					<div v-if="reopenedOpen" class="reopened-float-list">
						<div class="reopened-float-list-header">
							<span>Solicitações Reabertas</span>
							<i class="fas fa-times close-list-btn" @click="reopenedOpen = false"></i>
						</div>
						<div class="reopened-float-list-body">
							<transition-group name="list-item" tag="div">
							    <component
							        :is="item.protocol ? 'a' : 'div'"
							        v-for="item in reopenedList"
							        :key="item.protocol || item.client_id"
							        class="reopened-float-item"
							        :class="{ 'is-link': item.protocol }"
							        v-bind="item.protocol ? { href: reopenedLink(item), target: '_blank', rel: 'noopener' } : {}"
							    >
							        <div class="reopened-item-header">
							            <span class="reopened-item-type" :class="item.pedido ? 'type-pedido' : 'type-solic'">
							                {{ item.pedido ? 'Pedido' : 'Solicitação' }}
							            </span>
							            <span class="reopened-item-protocol">{{ item.protocol || '—' }}</span>
							        </div>
							        <div class="reopened-item-meta">
							            <span><i class="fas fa-clock"></i> {{ formatReopenedAt(item.reopened_at) }}</span>
							            <span><i class="fas fa-user"></i> {{ item.reopened_by || '—' }}</span>
							        </div>
							    </component>
							</transition-group>
						</div>
					</div>
				</transition>
				<button class="reopened-float-btn" @click="reopenedOpen = !reopenedOpen">
					<i class="fas fa-folder-open"></i>
					Solicitações reabertas
					<span class="reopened-badge">{{ reopenedList.length }}</span>
				</button>
			</div>
		</transition>
	</section>
</template>

<script>
// import ServiceLevelChart from './components/ServiceLevelChart.vue';
import AbandonedLevelChart from './components/AbandonedLevelChart.vue';
import QueueAvgTimeChart from './components/QueueAvgTimeChart.vue';
import QueueTotals from './components/QueueTotals.vue';
import ReceivedCallsChart from './components/ReceivedCallsChart.vue';
import OperatorsTable from './components/OperatorsTable.vue';
// import SatisfactionChart from './components/SatisfactionChart.vue';
import AttendanceReasonChart from './components/AttendanceReasonChart.vue';
import AttendanceChannel from './components/AttendanceChannel.vue';
import Requests from './components/Requests.vue';
import InfoCard from './components/InfoCard.vue';
// REMOVIDO: CsatChart — indicadores de CSAT telefonia e chat
// import CsatChart from './components/CsatChart.vue';
// INCLUÍDO: ReopenedRequests — gráfico de barras de solicitações reabertas
import ReopenedRequests from './components/ReopenedRequests.vue';
import Swal from 'sweetalert2';
import * as XLSX from 'xlsx';
import moment from 'moment';

export default {
	data() {
		return {
			isPolling: false,
			isLoading: false,
			excelLoading: false,
			interval: 30000,
			error: null,
			intervalId: null,
			start: null,
			end: null,
			type: 'realtime',
			selectedQueues: [],
			selectedChatQueues: [],
			queuesList: [],
			chatQueuesList: [],
			chartData: {
				cards: {
					answered: {},
					notAnswered: {},
					outgoing: {},
					received: {},
				},
				operators: {},
				serviceLevel: 0,
				abandonLevel: 0,
				queues: {},
				reasons: {
					phone: {},
					chat: {}
				},
				receivedCalls: {},
				survey: {
					phone: {},
					chat: {}
				},
				channels: {},
				requests: {
					channels: {},
					status: {},
					reopened: {}
				},
				reopenedRaw: [],
				orders: {}
			},
			dynamicChart:{
				queue: {
					length: 2,
					display: 1,
					mode: 'auto',
					autoIntervalId: null,
					autoInterval: 13033
				},
				survey: {
					length: 4,
					display: 1,
					mode: 'auto',
					autoIntervalId: null,
					autoInterval: 13033
				},
				channels: {
					length: 3,
					display: 1,
					mode: 'auto',
					autoIntervalId: null,
					autoInterval: 13033
				},
				requests: {
					length: 2,
					display: 1,
					mode: 'auto',
					autoIntervalId: null,
					autoInterval: 13033
				},
				levels: {
					length: 1,
					display: 1,
					mode: 'auto',
					autoIntervalId: null,
					autoInterval: 13033
				}
			},
			reopenedOpen: false,
		}
	},
	name: "Dashboard",
	components: {
		// ServiceLevelChart,
		AbandonedLevelChart,
		QueueAvgTimeChart,
		ReceivedCallsChart,
		OperatorsTable,
		// SatisfactionChart,
		AttendanceReasonChart,
		InfoCard,
		QueueTotals,
		AttendanceChannel,
		Requests,
		// CsatChart,
		ReopenedRequests
	},
	props: [],
	computed: {
		reopenedList() {
    		return this.chartData.reopenedRaw || [];
		},
	},
	async mounted() {
		await this.getUserQueues();
		await this.getUserChatQueues();
		await this.startPolling();

		const charts = Object.entries(this.dynamicChart)
		if(charts.length){
			charts.map(el => {
				if(el[1].mode === 'auto') this.startChartAutoMode(el[0])
			})
		}
	},
	beforeDestroy() {
		this.stopPolling()
		this.stopChartAutoMode()
	},
	methods: {
		async getUserQueues() {
			try {
				this.queuesList = [];
				await axios.get('/user-queues').then(({ data }) => {
					if (data?.length) {
						data.map(el => this.queuesList.push({ id: el.id, name: el.name }))
					}
					if (this.queuesList.length && !this.selectedQueues.length) {
						this.selectedQueues = this.queuesList
					}
				})
			} catch (err) {
				this.error = 'Erro ao obter filas de telefonia'
				console.error('Fetch queues error:', err)
			}
		},

		async getUserChatQueues() {
			try {
				this.chatQueuesList = [];
				await axios.get('/user-chat-queues').then(({ data }) => {
					if (data?.length) {
						data.map((el, index) => this.chatQueuesList.push({id: index, name:el}))
					}
					if (this.chatQueuesList.length && !this.selectedChatQueues.length) {
						this.selectedChatQueues = this.chatQueuesList
					}
				})
			} catch (err) {
				this.error = 'Erro ao obter filas de chat'
				console.error('Fetch chat queues error:', err)
			}
		},

		async fetchData(payload) {
			try {
				if(!this.isPolling){
					this.isLoading = true
				}

				this.error = null

				await axios.post('/dashboard/getDashboardData', payload).then(({ data }) => {
					if (data && Object.keys(data).length) {
						this.chartData.cards = data.cards;
						this.chartData.operators = data.operators;
						// this.chartData.serviceLevel = data.serviceLevel;
						this.chartData.abandonLevel = data.abandonLevel;
						this.chartData.queues = data.queues;
						this.chartData.reasons = data.reasons;
						this.chartData.receivedCalls = data.receivedCalls;
						this.chartData.survey = data.survey;
						this.chartData.channels = data.channels;
						this.chartData.requests = data.requests;
						this.chartData.reopenedRaw = data.reopenedRequests || [];
						this.chartData.orders = data.orders;
					}
				})
			} catch (err) {
				this.error = 'Erro ao carregar dados'
				console.error('Fetch data error:', err)
			} finally {
				this.isLoading = false
			}
		},

		async generateExcel(payload) {
			try {
				this.excelLoading = true;

				const payload = {
					start: this.start,
					end: this.end,
					type: this.type,
					selectedQueues: this.selectedQueues,
					selectedChatQueues: this.selectedChatQueues
				}

				let responseData = {};

				await axios.post('/dashboard/getDetailedData', payload).then(({ data }) => {
					if (data && Object.keys(data).length) {
						responseData = data
					}
				})

				if (Object.keys(responseData).length) {
					const workbook = XLSX.utils.book_new();
					let headerOrder = [];

					Object.entries(responseData).forEach(([sheetName, dataArray]) => {
						let formattedSheetName = 'planilha';
						const parsedArray = [];

						if (!Array.isArray(dataArray) || !dataArray.length) return

						switch (sheetName) {
							case 'outgoingCalls':
								formattedSheetName = 'Ligações realizadas'

								headerOrder = [
									'Data',
									'Telefone',
									'Operador',
									'Ramal',
									'Tempo de Atendimento'
								];

								dataArray.map(el => {
									if(typeof el === 'object' && Object.keys(el).length){
										let recordObj = {}
										Object.entries(el).map(obj => {
											if(obj[0] === 'calldate'){
												recordObj.Data = moment(obj[1]).format('DD/MM/YYYY - HH:mm:ss')
											}

											if(obj[0] === 'dst'){
												recordObj.Telefone = obj[1]
											}

											if(obj[0] === 'operator_name'){
												recordObj.Operador = obj[1]
											}

											if(obj[0] === 'src'){
												recordObj.Ramal = obj[1]
											}

											if(obj[0] === 'duration'){
												recordObj['Tempo de Atendimento'] = moment.utc(obj[1] * 1000).format('HH:mm:ss');
											}
										})
										parsedArray.push(recordObj)
									}
								})

								break;
							case 'answeredCalls':
								formattedSheetName = 'Ligações atendidas'

								headerOrder = [
									'Data',
									'Protocolo',
									'Telefone',
									'Operador',
									'Ramal',
									'Fila de Atendimento',
									'Tempo de Espera',
									'Tempo de Atendimento',
									'Avaliação',
									'Motivo da Avaliação'
								];

								dataArray.map(el => {
									if(typeof el === 'object' && Object.keys(el).length){
										let recordObj = {}
										Object.entries(el).map(obj => {
											if(obj[0] === 'time'){
												recordObj.Data = moment(obj[1]).format('DD/MM/YYYY - HH:mm:ss')
											}

											if(obj[0] === 'protocol'){
												recordObj.Protocolo = obj[1]
											}

											if(obj[0] === 'phone_number'){
												recordObj.Telefone = obj[1]
											}

											if(obj[0] === 'operator_name'){
												recordObj.Operador = obj[1]
											}

											if(obj[0] === 'agent'){
												recordObj.Ramal = obj[1]
											}
											
											if(obj[0] === 'queuename'){
												recordObj['Fila de Atendimento'] = obj[1]
											}

											if(obj[0] === 'data1'){
												recordObj['Tempo de Espera'] = moment.utc(obj[1] * 1000).format('HH:mm:ss');
											}

											if(obj[0] === 'data2'){
												recordObj['Tempo de Atendimento'] = moment.utc(obj[1] * 1000).format('HH:mm:ss');
											}

											if(obj[0] === 'nota'){
												let avaliacao;
												if(obj[1]){
													if(obj[1] == 1) avaliacao = 'Péssimo';
													if(obj[1] == 2) avaliacao = 'Ruim';
													if(obj[1] == 3) avaliacao = 'Regular';
													if(obj[1] == 4) avaliacao = 'Bom';
													if(obj[1] == 5) avaliacao = 'Ótimo';
												}
												recordObj.Avaliação = avaliacao
											}

											if(obj[0] === 'nota1'){
												let motivo;
												if(obj[1]){
													if(obj[1] == 1) motivo = 'Prazo';
													if(obj[1] == 2) motivo = 'Atendimento';
													if(obj[1] == 3) motivo = 'Dificuldade no processo';
													if(obj[1] == 4) motivo = 'Resolução';
													if(obj[1] == 5) motivo = 'Outros motivos';
												}
												recordObj['Motivo da Avaliação'] = motivo
											}
										})
										parsedArray.push(recordObj)
									}
								})

								break;
							case 'notAnsweredCalls':
								formattedSheetName = 'Ligações perdidas'

								headerOrder = [
									'Data',
									'Protocolo',
									'Telefone',
									'Operador',
									'Ramal',
									'Fila de Atendimento',
									'Tempo de Espera'
								];

								dataArray.map(el => {
									if(typeof el === 'object' && Object.keys(el).length){
										let recordObj = {}
										Object.entries(el).map(obj => {
											if(obj[0] === 'time'){
												recordObj.Data = moment(obj[1]).format('DD/MM/YYYY - HH:mm:ss')
											}

											if(obj[0] === 'protocol'){
												recordObj.Protocolo = obj[1]
											}

											if(obj[0] === 'phone_number'){
												recordObj.Telefone = obj[1]
											}

											if(obj[0] === 'operator_name'){
												recordObj.Operador = obj[1]
											}

											if(obj[0] === 'agent'){
												recordObj.Ramal = obj[1]
											}
											
											if(obj[0] === 'queuename'){
												recordObj['Fila de Atendimento'] = obj[1]
											}

											if(obj[0] === 'data1'){
												const duration = moment.duration(obj[1]);
												recordObj['Tempo de Espera'] = moment.utc(duration.asMilliseconds()).format('HH:mm:ss');
											}
										})
										parsedArray.push(recordObj)
									}
								})

								break;
							case 'abandonedCalls':
								formattedSheetName = 'Ligações abandonadas'

								headerOrder = [
									'Data',
									'Protocolo',
									'Telefone',
									'Fila de Atendimento',
									'Posição Inicial na Fila',
									'Posição Final na Fila',
									'Tempo de Espera',
								];

								dataArray.map(el => {
									if(typeof el === 'object' && Object.keys(el).length){
										let recordObj = {}
										Object.entries(el).map(obj => {
											if(obj[0] === 'time'){
												recordObj.Data = moment(obj[1]).format('DD/MM/YYYY - HH:mm:ss')
											}

											if(obj[0] === 'protocol'){
												recordObj.Protocolo = obj[1]
											}

											if(obj[0] === 'phone_number'){
												recordObj.Telefone = obj[1]
											}
											
											if(obj[0] === 'queuename'){
												recordObj['Fila de Atendimento'] = obj[1]
											}
											
											if(obj[0] === 'data2'){
												recordObj['Posição Inicial na Fila'] = obj[1]
											}

											if(obj[0] === 'data1'){
												recordObj['Posição Final na Fila'] = obj[1]
											}

											if(obj[0] === 'data3'){
												recordObj['Tempo de Espera'] = moment.utc(obj[1] * 1000).format('HH:mm:ss');
											}
										})
										parsedArray.push(recordObj)
									}
								})

								break;
							case 'requests':
								formattedSheetName = 'Solicitações'

								headerOrder = [
									'Data',
									'Criado por',
									'Alterado por',
									'Chamado',
									'Protocolo',
									'Telefone',
									'CNPJ',
									'Nome do Ponto',
									'Nome',
									'Cliente',
									'Tipo',
									'Assunto',
									'Área Responsável',
									'Status'
								];

								dataArray.map(el => {
									if(typeof el === 'object' && Object.keys(el).length){
										let recordObj = {}
										Object.entries(el).map(obj => {
											if(obj[0] === 'created_at'){
												recordObj.Data = moment(obj[1]).format('DD/MM/YYYY - HH:mm:ss')
											}

											if(obj[0] === 'created_by'){
												recordObj['Criado por'] = obj[1]
											}

											if(obj[0] === 'updated_by'){
												recordObj['Alterado por'] = obj[1]
											}

											if(obj[0] === 'protocol'){
												recordObj.Chamado = obj[1]
											}

											if(obj[0] === 'attProtocol'){
												recordObj.Protocolo = obj[1]
											}

											if(obj[0] === 'client_id'){
												recordObj.Telefone = obj[1].indexOf('@') !== -1 ? obj[1].substring(0, obj[1].indexOf('@')) : obj[1];
											}
											

											if(obj[0] === 'cnpj'){
												recordObj.CNPJ = obj[1]
											}

											if(obj[0] === 'clientInfo'){
												recordObj['Nome do Ponto'] = obj[1].NomePonto
											}
											
											if(obj[0] === 'name'){
												if(obj[1] === 'RequestByPhone'){
													recordObj.Nome = 'Via Telefone'
												} else {
													recordObj.Nome = obj[1]
												}
											}

											if(obj[0] === 'client'){
												if(obj[1] === 'RequestByPhone'){
													recordObj.Cliente = 'Via Telefone'
												} else {
													recordObj.Cliente = obj[1]
												}
											}

											if(obj[0] === 'pedido'){
												recordObj.Tipo = 'Pedido'

												recordObj.Assunto = obj[1].solicitacao

												const tipo = typeof obj[1].tipo === 'string' 
												    ? (obj[1].tipo.startsWith('[') ? JSON.parse(obj[1].tipo) : [obj[1].tipo])
												    : obj[1].tipo;

												recordObj.Status = obj[1].status
											}

											if(obj[0] === 'relato'){
												if(obj[1].tipo){
													if(obj[1].tipo === 'tecnicascoce'){
														recordObj.Tipo = 'Técnicas';
													} else {
														recordObj.Tipo = obj[1].tipo.charAt(0).toUpperCase() + obj[1].tipo.slice(1);
													}
												} else {
													recordObj.Tipo = ''
												}

												recordObj.Assunto = obj[1].motivo ? `${obj[1].motivo} - ${obj[1].assunto}` : obj[1].assunto

												recordObj.Status = obj[1].status
											}

											if(obj[0] === 'sector'){
												recordObj['Área Responsável'] = obj[1]
											}
										})
										parsedArray.push(recordObj)
									}
								})
								break;
							case 'whatsapp': 
								formattedSheetName = 'Whatsapp'
								
								headerOrder = [
									'Data',
									'Protocolo',
									'Telefone',
									'CNPJ',
									'Banco',
									'Agência',
									'Nome do Ponto',
									'Nome',
									'Cliente',
									'Fila de Atendimento',
									'Operador',
									'Avaliação',
									'Motivo da Insatisfação'
								];
								
								dataArray.map(el => {
									if(typeof el === 'object' && Object.keys(el).length){
										let recordObj = {}
										Object.entries(el).map(obj => {
											if(obj[0] === 'created_at'){
												recordObj.Data = moment(obj[1]).format('DD/MM/YYYY - HH:mm:ss')
											}
										
											if(obj[0] === 'protocol'){
												recordObj.Protocolo = obj[1]
											}
										
											if(obj[0] === 'client_id'){
												recordObj.Telefone = obj[1].indexOf('@') !== -1 ? obj[1].substring(0, obj[1].indexOf('@')) : obj[1];
											}
														
											if(obj[0] === 'cnpj'){
												recordObj.CNPJ = obj[1]
											}
										
											if(obj[0] === 'bankName'){
												recordObj.Banco = obj[1]
											}
										
											if(obj[0] === 'agencia'){
												recordObj.Agência = obj[1]
											}
										
											if(obj[0] === 'clientInfo'){
												recordObj['Nome do Ponto'] = obj[1].NomePonto
											}
											
											if(obj[0] === 'name'){
												recordObj.Nome = obj[1]
											}
										
											if(obj[0] === 'client'){
												recordObj.Cliente = obj[1]
											}
										
											if(obj[0] === 'queue'){
												recordObj['Fila de Atendimento'] = obj[1]
											}
										
											if(obj[0] === 'operador_nome'){
												recordObj.Operador = obj[1]
											}
										
											if(obj[0] === 'avaliacao'){
												recordObj.Avaliação = obj[1]
											}
										
											if(obj[0] === 'motivo'){
												recordObj['Motivo da Insatisfação'] = obj[1]
											}
										})
										parsedArray.push(recordObj)
									}
								})
								break;
							case 'reopenedRequests':
    							formattedSheetName = 'Reabertas'

    							headerOrder = [
    							    'Data de Reabertura',
									'Reaberto por',
    							    'Protocolo',
    							    'Tipo',
    							    'Canal'
    							];

    							dataArray.map(el => {
    							    if(typeof el === 'object' && Object.keys(el).length){
    							        let recordObj = {}
									
    							        recordObj['Data de Reabertura'] = el.reopened_at 
    							            ? moment(el.reopened_at).format('DD/MM/YYYY - HH:mm:ss') 
    							            : ''
									
    							        recordObj.Protocolo = el.protocol ?? ''
    							        recordObj['Reaberto por'] = el.reopened_by ?? ''
									
    							        if(el.pedido){
    							            recordObj.Tipo = 'Pedido'
    							        } else if(el.relato){
    							            if(el.relato.tipo === 'tecnicascoce'){
    							                recordObj.Tipo = 'Técnicas'
    							            } else if(el.relato.tipo){
    							                recordObj.Tipo = el.relato.tipo.charAt(0).toUpperCase() + el.relato.tipo.slice(1)
    							            } else {
    							                recordObj.Tipo = ''
    							            }
    							        }
									
    							        if(el.client === 'RequestByPhone'){
    							            recordObj.Canal = 'Telefone'
    							        } else if(el.client_id && el.client_id.indexOf('operatorForm') !== -1){
    							            recordObj.Canal = 'Formulário'
    							        } else {
    							            recordObj.Canal = 'Whatsapp'
    							        }
									
    							        parsedArray.push(recordObj)
    							    }
    							})
    							break;
							default:
								break;
						}

						if (Array.isArray(parsedArray) && parsedArray.length) {
							const worksheet = XLSX.utils.json_to_sheet(parsedArray, { header: headerOrder });
							XLSX.utils.sheet_add_aoa(worksheet, [headerOrder], { origin: "A1" });
							worksheet['!cols'] = headerOrder.map(() => ({ wch: 19 }));
							worksheet['!autofilter'] = { ref: worksheet['!ref'] };
							XLSX.utils.book_append_sheet(workbook, worksheet, formattedSheetName);
						}
					});
				
					XLSX.writeFile(workbook, 'dashboard-dados.xlsx');
				}
			} catch (err) {
				this.error = 'Erro ao gerar excel'
				console.error('Generate Excel error:', err)
			} finally {
				this.excelLoading = false
			}
		},

		startPolling() {
			const payload = {
				start: this.start,
				end: this.end,
				type: this.type,
				selectedQueues: this.selectedQueues,
				selectedChatQueues: this.selectedChatQueues
			}
			this.stopPolling()
			this.fetchData({...payload})

			this.isPolling = true;

			if (this.isPolling) {
				this.intervalId = setInterval(() => {
					this.fetchData({...payload})
				}, this.interval)
			}
		},

		stopPolling() {
			this.isPolling = false;
			if (this.intervalId) {
				clearInterval(this.intervalId)
				this.intervalId = null
			}
		},

		restartPolling() {
			if (this.isPolling) {
				this.startPolling()
			}
		},

		togglePolling() {
			this.isPolling = !this.isPolling
			if (this.isPolling) {
				this.startPolling()
			} else {
				this.stopPolling()
			}
		},

		resetChartData() {
			this.chartData = {
				cards: {
					answered: {},
					notAnswered: {},
					outgoing: {},
					received: {},
				},
				operators: {},
				serviceLevel: 0,
				abandonLevel: 0,
				queues: {},
				reasons: {
					phone: {},
					chat: {}
				},
				receivedCalls: {},
				survey: {
					phone: {},
					chat: {}
				},
				channels: {},
				requests: {
					channels: {},
					status: {},
					// INCLUÍDO: reopened
					reopened: {}
				},
				orders: {}
			}
		},

		async applyFilters(){
			if (!this.selectedQueues.length) {
		    	Swal.fire({
		    	    type: 'error',
		    	    title: 'Atenção',
		    	    text: 'Você precisa selecionar pelo menos uma fila de telefonia.',
		    	    confirmButtonText: 'OK'
		    	});
    			return;
			}
			if (!this.selectedChatQueues.length) {
		    	Swal.fire({
		    	    type: 'error',
		    	    title: 'Atenção',
		    	    text: 'Você precisa selecionar pelo menos uma fila de chat.',
		    	    confirmButtonText: 'OK'
		    	});
    			return;
			}
			if(this.type === 'report' && (!this.start || !this.end)){
				Swal.fire({
		        	type: 'error',
		        	title: 'Atenção',
		        	text: 'Você precisa selecionar um período para buscar o relatório!',
		        	confirmButtonText: 'OK'
		    	});
    			return;
			}

			const payload = {
				start: this.start,
				end: this.end,
				type: this.type,
				selectedQueues: this.selectedQueues,
				selectedChatQueues: this.selectedChatQueues
			}

			if (this.type === 'realtime') {
				await this.startPolling()
			} else {
				await this.stopPolling();
				await this.fetchData({...payload});
			}
			this.closeFilters()
		},

		async resetFilters(){
			this.type = 'realtime';
			this.selectedQueues = this.queuesList;
			this.selectedChatQueues = this.chatQueuesList;
			this.start = null;
			this.end = null;
			this.applyFilters()
		},

		closeFilters() {
			this.$root.$emit('bv::toggle::collapse', 'collapse-dashboard-filters')
		},

		changeChartMode(type){
			if(this.dynamicChart[type].mode === 'fixed'){
				this.dynamicChart[type].mode = 'auto';
				this.startChartAutoMode(type);
			} else if(this.dynamicChart[type].mode === 'auto') {
				this.dynamicChart[type].mode = 'fixed';
				this.stopChartAutoMode(type);
			}
		},

		changeChartDisplay(type){
			const length = this.dynamicChart[type].length;
			if(this.dynamicChart[type].display < length){
				this.dynamicChart[type].display++
			} else {
				this.dynamicChart[type].display = 1;
			}
		},

		startChartAutoMode(type) {
			this.stopChartAutoMode(type);
			
			if (this.dynamicChart[type].mode === 'auto') {
				this.dynamicChart[type].autoIntervalId = setInterval(() => {
					this.changeChartDisplay(type);
				}, this.dynamicChart[type].autoInterval);
			}
		},

		stopChartAutoMode(type) {
			if (this.dynamicChart[type].autoIntervalId) {
				clearInterval(this.dynamicChart[type].autoIntervalId);
				this.dynamicChart[type].autoIntervalId = null;
			}
		},
		formatReopenedAt(val) {
		    if (!val) return '—';
		    return moment(val).format('DD/MM/YYYY HH:mm:ss');
		},
		reopenedLink(item) {
		    let tab = 'solicitacao';
		    if (item.pedido) {
		        tab = 'pedido';
		    } else if (item.relato && (item.relato.tipo === 'tecnicascoce' || item.system === 'cce')) {
		        tab = 'tecnicas';
		    }
		    return `/solicitacoes?protocol=${encodeURIComponent(item.protocol)}&tab=${tab}`;
		},
	},
}
</script>

<style lang="scss">
.custom-topbar {
	height: 40px;
	margin: 5px;
	display: flex;
	justify-content: space-between;

	button {
		border-radius: 10px !important;
		font-weight: bold;
		box-shadow: 1px 1px 3px 0px gray;

		text-shadow: 1px 1px 3px rgba(0, 0, 0, 0.7) !important;
	}

	.mode-indicator {
		color: #1c869a;
    	font-size: medium;
    	padding: 10px 5px;
    	align-items: center;
    	display: flex;
		gap: 5px;

		span {
			font-weight: bold;
		}

		.realtime {
			color: #cf1111;
		}

		.report {
			color: goldenrod;
		}
	}
}

.collapse-wrapper {
	position: absolute;
	top: 65px;
	left: 0;
	width: 100vw;
	z-index: 998;

	.filters-collapse {
		box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
		border-bottom: 1px solid #dee2e6;

		.filters-card {
			.card-body {
				padding: 0;
			}

			margin: 0;
			height: fit-content;
			background-color: #e9e9e9f5;
			display: flex;
			flex-direction: column;

			.filters-content {
				padding: 20px;
				display: flex;
				gap: 20px;
				width: 100%;

				.button-group {
					display: flex;
					flex-direction: column;
					flex: 1;
					gap: 20px;
					justify-content: center;

					button {
						padding: 5px 10px;
						border-radius: 10px !important;
						font-weight: bold;
						box-shadow: 1px 1px 3px 0px gray;

						text-shadow: 1px 1px 3px rgba(0, 0, 0, 0.7) !important;
    
    					i {
    					    text-shadow: 1px 1px 3px rgba(0, 0, 0, 0.7) !important;
    					}
					}
				}

				.filter-group {
					display: flex;
					flex-direction: column;
					flex: 3;
					background-color: #f5f5f5;
					border-radius: 10px;
					padding: 5px 15px;
					gap: 15px;
					align-items: flex-start;
					justify-content: flex-start;
					box-shadow: 1px 1px 7px 0px gray;

					
					span {
						font-size: 14px;
						font-weight: bold;
						white-space: nowrap;
						
						.multiselect__single {
								font-size: 14px;
								color: #333;
								font-weight: 500;
						}
					}

					.type-select-group {
						display: flex;
						justify-content: flex-start;
						gap: 15px;

						.radio-group {
							display: flex;
							flex: 1;
							gap: 5px;
							align-items: center;

							label {
								margin: 0 !important;
								white-space: nowrap;
							}

							input {
								width: 20px;
								cursor: pointer;

								&:focus {
									outline: none;
									box-shadow: none;
								}
							}
						}
					}


					.date-group {
						display: flex;
						flex: 1;
						gap: 5px;
						border-left: 3px solid #8080804a;
						padding: 0 10px;

						div {
							display: flex;
							gap: 7px;
							align-items: center;
						}

						input {
							border-radius: 5px !important;
						}
					}
				}

			}
			.close-filter-button {
				background: none;
    			color: #cc4141;
    			padding: 5px 10px;
    			border-radius: 10px !important;
    			font-weight: bold;
    			border: none;
    			width: 100%;
				transition: 0.4s;

				&:hover {
					background-color: rgba(211, 211, 211, 0.479);
				}
			}
		}
	}
}

.loading-container {
	height: calc(100vh - 140px);
	display: flex;
	justify-content: center;
	border: 1px solid lightgray;
	box-shadow: 1px 1px 4px 0 lightgray;
	border-radius: 10px;

	.loading-content {
		display: flex;
		flex: 1;
		flex-direction: column;
		align-items: center;
		align-self: center;
		padding: 40px;
	}

	.loading-icon {
		font-size: 48px;
		color: #1c869a;
		margin-bottom: 20px;
	}

	.loading-text {
		font-size: 24px;
		color: #495057;
		margin: 0;
		font-weight: bold;
		text-align: center;
	}
}

.dashboard-container {
	height: calc(100vh - 140px);
	display: grid;
	grid-template-columns: minmax(0, 2fr) minmax(0, 1fr);
	max-width: 100vw;
	overflow: hidden;

	@media (max-width: 1000px) {
		grid-template-columns: minmax(0, 1fr);
		grid-template-rows: auto auto;
	}

	.custom-panel {
		display: flex;
		flex-direction: column;
		max-height: calc(100vh - 150px);

		.chart-container {
			border: 1px solid lightgray;
			box-shadow: 1px 1px 4px 0 lightgray;
			padding: 2px;
			border-radius: 10px;
			margin: 4px;
			overflow-y: auto;
			overflow-x: hidden;
			position: relative;
			display: flex;
			flex-direction: column;

			&::-webkit-scrollbar {
				padding: 1px;
			}

			&::-webkit-scrollbar-thumb {
				background: #7db6c9;
				border-radius: 5px;
			}

			scrollbar-width: thin;
			scrollbar-color: #7db6c9 transparent;

			.toggle-chart-buttons {
				display: flex;
				flex-direction: column;
    			gap: 3px;
				align-items: flex-start;
    			position: absolute;
    			z-index: 99;
    			width: fit-content;
    			padding: 3px;

				div {
					opacity: 0.4;
    				cursor: pointer;
    				color: white;
    				width: 70px;
    				display: flex;
    				gap: 3px;
    				justify-content: center;
					transition: 0.4s ease-in-out;
				}
				
				.mode-icon {
					&:hover {
						opacity: 1;
					}

    				background-color: #cf1111;
				}

				.change-icon {
					&:hover {
						opacity: 1;
					}
					background-color: #1c869a;
				}
			}
		}

		.horizontal-container {
			display: flex;
			gap: 10px;
		}


		.chart-lg {
			flex: 3;
		}

		.chart-md {
			flex: 2;
		}

		.chart-sm {
			flex: 1;
		}

		.no-overflow {
			overflow: hidden !important;
		}
	}
}

@media (max-width: 768px) {
	.collapse-wrapper {
		.filters-collapse {
			.filters-card {
				.filters-content {
					padding: 15px;
				}
			}
		}
	}
}

.reopened-float-wrapper {
	position: fixed;
	bottom: 24px;
	right: 24px;
	z-index: 2000;
	display: flex;
	flex-direction: column;
	align-items: flex-end;
	gap: 10px;

	.reopened-float-btn {
		display: flex;
		align-items: center;
		gap: 8px;
		background: #1c869a;
		color: #fff;
		border: none;
		border-radius: 10px !important;
		padding: 10px 20px 10px 16px;
		font-size: 13px;
		font-weight: bold;
		cursor: pointer;
		box-shadow: 0 4px 16px rgba(0,0,0,0.22);
		transition: background 0.2s, box-shadow 0.2s, transform 0.15s;
		position: relative;

		&:hover {
			background: #155f70;
			box-shadow: 0 6px 22px rgba(0,0,0,0.28);
			transform: translateY(-2px);
		}

		.reopened-badge {
			background: #cf1111;
			color: #fff;
			font-size: 11px;
			font-weight: bold;
			border-radius: 50%;
			min-width: 20px;
			height: 20px;
			display: inline-flex;
			align-items: center;
			justify-content: center;
			padding: 0 4px;
			margin-left: 4px;
			box-shadow: 0 1px 4px rgba(0,0,0,0.3);
		}
	}

	.reopened-float-list {
		background: #fff;
		border-radius: 12px;
		box-shadow: 0 8px 32px rgba(0,0,0,0.18);
		width: 320px;
		max-height: 400px;
		display: flex;
		flex-direction: column;
		overflow: hidden;
		border: 1px solid #e0e0e0;

		.reopened-float-list-header {
			display: flex;
			justify-content: space-between;
			align-items: center;
			padding: 12px 16px;
			background: #1c869a;
			color: #fff;
			font-weight: bold;
			font-size: 13px;

			.close-list-btn {
				cursor: pointer;
				opacity: 0.8;
				transition: opacity 0.2s;
				&:hover { opacity: 1; }
			}
		}

		.reopened-float-list-body {
			overflow-y: auto;
			flex: 1;
			padding: 8px;
			display: flex;
			flex-direction: column;
			gap: 6px;

			scrollbar-width: thin;
			scrollbar-color: #7db6c9 transparent;
			&::-webkit-scrollbar-thumb {
				background: #7db6c9;
				border-radius: 5px;
			}
		}

		.reopened-float-item {
			background: #f8f9fa;
			border-radius: 8px;
			padding: 10px 12px;
			margin: 7px 0;
			border-left: 3px solid #1c869a;
			display: flex;
			flex-direction: column;
			gap: 5px;
			transition: background 0.2s;

			&:hover { background: #edf6f9; }

			&.is-link {
			    cursor: pointer;
			    text-decoration: none;
			    color: inherit;
			}

			.reopened-item-header {
				display: flex;
				justify-content: space-between;
				align-items: center;

				.reopened-item-type {
					font-size: 11px;
					font-weight: bold;
					border-radius: 20px;
					padding: 2px 10px;
					color: #fff;

					&.type-pedido { background: #e07b39; }
					&.type-solic  { background: #c0392b; }
				}

				.reopened-item-protocol {
					font-size: 12px;
					color: #555;
					font-weight: 600;
				}
			}

			.reopened-item-meta {
				display: flex;
				gap: 12px;
				font-size: 11px;
				color: #777;

				i { margin-right: 3px; color: #1c869a; }
			}
		}
	}
}

// Animações do botão/lista flutuante
.fade-float-enter-active, .fade-float-leave-active { transition: opacity 0.3s; }
.fade-float-enter, .fade-float-leave-to { opacity: 0; }

.slide-up-enter-active {
	transition: all 0.25s cubic-bezier(0.34, 1.56, 0.64, 1);
}
.slide-up-leave-active {
	transition: all 0.18s ease-in;
}
.slide-up-enter {
	opacity: 0;
	transform: translateY(16px) scale(0.97);
}
.slide-up-leave-to {
	opacity: 0;
	transform: translateY(10px) scale(0.97);
}

.list-item-enter-active, .list-item-leave-active {
	transition: all 0.3s ease;
}
.list-item-enter {
	opacity: 0;
	transform: translateX(20px);
}
.list-item-leave-to {
	opacity: 0;
	transform: translateX(-20px);
}
</style>